import {
  Paper,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import { ResponsiveLine } from "@nivo/line";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { MetricGraphProps } from "../types";

const ITEMS_PER_PAGE = 20;

export default function MetricGraph({
  metricData,
  onClose,
  isFetchingMetricData,
}: MetricGraphProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = Math.ceil(metricData.length / ITEMS_PER_PAGE);

  const paginatedData = metricData.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  const chartData = [
    {
      id: metricData[0]?.metric,
      data: paginatedData.map((entry) => ({
        x: entry.date.split("T")[0],
        y: entry.data.length
          ? entry.data.reduce((sum, value) => sum + value, 0) /
            entry.data.length
          : 0,
      })),
    },
  ];

  return (
    <Paper
      sx={{
        position: "absolute",
        top: 16,
        left: 16,
        width: 600,
        zIndex: 9999,
        transition: "width 0.3s ease-in-out",
        pointerEvents: "auto", // Enable pointer events for the button
      }}
      elevation={3}
    >
      <Box
        sx={{
          p: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle1" sx={{ ml: 1 }}>
          Metric History Graph
        </Typography>
        <IconButton onClick={onClose} size="small">
          <X />
        </IconButton>
      </Box>

      <Box
        sx={{
          height: 300,
          p: 2,
          width: "calc(100% - 16px)",
          minWidth: 500,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isFetchingMetricData ? (
          <CircularProgress />
        ) : (
          <ResponsiveLine
            data={chartData}
            margin={{ top: 20, right: 50, bottom: 80, left: 60 }}
            xScale={{ type: "point" }}
            yScale={{
              type: "linear",
              min: 0,
              max: "auto",
              stacked: true,
              reverse: false,
            }}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: "Date",
              legendOffset: 75,
              legendPosition: "middle",
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: "Average Metric",
              legendOffset: -50,
              legendPosition: "middle",
            }}
            colors={{ scheme: "nivo" }}
            lineWidth={3}
            enablePoints={true}
            enableArea={true}
            enableGridX={false}
            enableGridY={true}
            // enablePointLabel={true}
            useMesh={true}
            tooltip={({ point }) => (
              <strong>
                {point.serieId}: {point.data.yFormatted}
              </strong>
            )}
          />
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          pb: 1,
        }}
      >
        <IconButton
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
          disabled={currentPage === 0}
        >
          <ChevronLeft />
        </IconButton>
        <Typography variant="body2" sx={{ mx: 2 }}>
          Page {currentPage + 1} of {totalPages}
        </Typography>
        <IconButton
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
          }
          disabled={currentPage >= totalPages - 1}
        >
          <ChevronRight />
        </IconButton>
      </Box>
    </Paper>
  );
}
